import { useEffect, useRef } from 'react';

/**
 * Premium dark 3D background:
 *   - near-black base
 *   - rotating low-poly icosahedron wireframes in corners
 *   - subtle star field
 *   - faint floor reflection at the bottom
 *   - mouse parallax
 */
const ThreeBackground = ({ theme = 'dark' }) => {
  const canvasRef = useRef(null);
  const mouseRef = useRef({ x: 0, y: 0, tx: 0, ty: 0 });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let rafId;
    let w, h;

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = canvas.clientWidth;
      h = canvas.clientHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener('resize', resize);

    const onMove = (e) => {
      mouseRef.current.tx = (e.clientX / window.innerWidth - 0.5) * 2;
      mouseRef.current.ty = (e.clientY / window.innerHeight - 0.5) * 2;
    };
    window.addEventListener('mousemove', onMove);

    const isDark = theme === 'dark';

    // ── Icosahedron wireframe geometry ──
    // 12 vertices of icosahedron
    const phi = (1 + Math.sqrt(5)) / 2;
    const icoVerts = [
      [-1,  phi, 0], [ 1,  phi, 0], [-1, -phi, 0], [ 1, -phi, 0],
      [0, -1,  phi], [0,  1,  phi], [0, -1, -phi], [0,  1, -phi],
      [ phi, 0, -1], [ phi, 0,  1], [-phi, 0, -1], [-phi, 0,  1],
    ];
    // Edges (pairs of vertex indices)
    const icoEdges = [
      [0,1],[0,5],[0,7],[0,10],[0,11],
      [1,5],[1,7],[1,8],[1,9],
      [2,3],[2,4],[2,6],[2,10],[2,11],
      [3,4],[3,6],[3,8],[3,9],
      [4,5],[4,9],[4,11],
      [5,9],[5,11],
      [6,7],[6,8],[6,10],
      [7,8],[7,10],
      [8,9],[10,11],
    ];

    const diamonds = [
      { cx: 0.12, cy: 0.18, size: 180, rotSpeed: 0.0025, phase: 0 },
      { cx: 0.88, cy: 0.15, size: 140, rotSpeed: 0.0018, phase: 1.3 },
      { cx: 0.92, cy: 0.82, size: 200, rotSpeed: -0.0022, phase: 2.1 },
      { cx: 0.08, cy: 0.78, size: 160, rotSpeed: -0.0016, phase: 0.8 },
    ];

    // ── Star field ──
    const stars = Array.from({ length: 120 }, () => ({
      x: Math.random(),
      y: Math.random(),
      r: Math.random() * 1.2 + 0.3,
      tw: Math.random() * Math.PI * 2, // twinkle phase
    }));

    let time = 0;

    const drawDiamond = (d, mx, my) => {
      const cx = d.cx * w + mx * 20;
      const cy = d.cy * h + my * 15;
      const ay = time * d.rotSpeed + d.phase;
      const ax = time * d.rotSpeed * 0.6 + d.phase;

      // Project each vertex
      const projected = icoVerts.map(([vx, vy, vz]) => {
        // rotate Y
        let x = vx * Math.cos(ay) - vz * Math.sin(ay);
        let z = vx * Math.sin(ay) + vz * Math.cos(ay);
        let y = vy;
        // rotate X
        const y2 = y * Math.cos(ax) - z * Math.sin(ax);
        const z2 = y * Math.sin(ax) + z * Math.cos(ax);
        y = y2; z = z2;

        const focal = 4;
        const scale = focal / (focal + z);
        return {
          sx: cx + x * d.size * 0.5 * scale,
          sy: cy + y * d.size * 0.5 * scale,
          depth: z,
        };
      });

      const lineColor = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(15,23,42,0.05)';
      ctx.strokeStyle = lineColor;
      ctx.lineWidth = 1;
      for (const [a, b] of icoEdges) {
        const pa = projected[a];
        const pb = projected[b];
        ctx.beginPath();
        ctx.moveTo(pa.sx, pa.sy);
        ctx.lineTo(pb.sx, pb.sy);
        ctx.stroke();
      }

      // Vertex dots
      ctx.fillStyle = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(15,23,42,0.08)';
      for (const p of projected) {
        ctx.beginPath();
        ctx.arc(p.sx, p.sy, 1.4, 0, Math.PI * 2);
        ctx.fill();
      }
    };

    const render = () => {
      time += 16;
      mouseRef.current.x += (mouseRef.current.tx - mouseRef.current.x) * 0.03;
      mouseRef.current.y += (mouseRef.current.ty - mouseRef.current.y) * 0.03;

      // 1) Dark base
      ctx.fillStyle = isDark ? '#0a0a0f' : '#dfe3ec';
      ctx.fillRect(0, 0, w, h);

      // 2) Star field
      const mx = mouseRef.current.x;
      const my = mouseRef.current.y;
      const starColor = isDark ? '255,255,255' : '30,41,59';
      for (const s of stars) {
        const twinkle = 0.4 + 0.6 * (0.5 + 0.5 * Math.sin(time * 0.002 + s.tw));
        const alpha = (isDark ? 0.35 : 0.15) * twinkle;
        const sx = s.x * w + mx * 8;
        const sy = s.y * h + my * 6;
        ctx.fillStyle = `rgba(${starColor}, ${alpha})`;
        ctx.beginPath();
        ctx.arc(sx, sy, s.r, 0, Math.PI * 2);
        ctx.fill();
      }

      // 3) Corner wireframe diamonds
      for (const d of diamonds) drawDiamond(d, mx, my);

      // 4) Floor reflection plane at bottom
      const floorH = h * 0.35;
      const grad = ctx.createLinearGradient(0, h - floorH, 0, h);
      if (isDark) {
        grad.addColorStop(0, 'rgba(255,255,255,0)');
        grad.addColorStop(1, 'rgba(255,255,255,0.025)');
      } else {
        grad.addColorStop(0, 'rgba(15,23,42,0)');
        grad.addColorStop(1, 'rgba(15,23,42,0.02)');
      }
      ctx.fillStyle = grad;
      ctx.fillRect(0, h - floorH, w, floorH);

      rafId = requestAnimationFrame(render);
    };
    render();

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', onMove);
    };
  }, [theme]);

  return <canvas ref={canvasRef} className="d-three-bg" />;
};

export default ThreeBackground;
